import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * A back button that returns where the reader actually came from.
 *
 * <p>These buttons used to navigate to a freshly built URL — the project page, the sprint list —
 * which meant they could never carry anything the reader had set on the way in. Open an item from
 * a filtered list, press back, and the list came up unfiltered, on page one, on whichever tab the
 * URL named. The filters were not lost: the button was never going back, it was going somewhere
 * that merely looked the same.
 *
 * <p>Two dozen screens link to a work item — lists, reports, the watchlist, the sprint board, a
 * parent item. Tagging every one of them with where it came from is a change that is wrong the
 * first time somebody adds the twenty-fifth. The browser already records it, so this asks the
 * browser.
 *
 * <p>{@code hasHistory} is what makes the label honest. Somebody who followed a link from an
 * email has nothing behind them, and a "Back" that leaves the application is worse than no
 * button; they get the fallback — the item's project — named as itself. The index comes from
 * React Router, which keeps one in the history entry it owns; anything else means this entry was
 * not pushed by this application.
 */
export function useGoBack(fallback: string): { goBack: () => void; hasHistory: boolean } {
  const navigate = useNavigate();

  const hasHistory =
    typeof window !== "undefined" &&
    typeof (window.history.state as { idx?: number } | null)?.idx === "number" &&
    (window.history.state as { idx: number }).idx > 0;

  const goBack = useCallback(() => {
    if (hasHistory) {
      navigate(-1);
      return;
    }
    navigate(fallback);
  }, [hasHistory, navigate, fallback]);

  return { goBack, hasHistory };
}
